import "./OUpload.scss";
import React, { useState, useEffect, useRef } from "react";
import { uploadFile } from "../services/api";

const OUpload = ({
  multiple = false,
  imagesBaseUrl = "https://songstarktours.s3.ap-northeast-2.amazonaws.com",
  value = "",
  text = "Drag and drop image here, or click here to add image",
  size = "md",
  onImagesUploaded,
  onStartLoader,
  onStopLoader,
}) => {
  const [images, setImages] = useState(
    Array.isArray(value) ? value : value ? [value] : []
  );
  const [imagesToUpload, setImagesToUpload] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (value && value !== "") {
      setImages(Array.isArray(value) ? value : [value]);
    } else {
      setImages([]);
    }
  }, [value]);

  const handleUpload = (e) => {
    const files = e?.target.files;

    if (files && files.length > 0) {
      const newImages = Array.from(files);
      setImagesToUpload(newImages);

      // Upload each image
      newImages.forEach((image) => {
        uploadImage(image);
      });
    }
  };

  const removeImage = (image) => {
    const updatedImages = images.filter((img) => img !== image);
    setImages(updatedImages);
    onImagesUploaded?.(multiple ? updatedImages : updatedImages[0] || "");
  };

  const uploadImage = async (image) => {
    try {
      setUploading(true);
      onStartLoader?.();

      const formData = new FormData();
      formData.append("file", image);

      // Use our API service for upload - backend expects /api/v1/upload
      const response = await uploadFile("/upload", formData);

      if (
        response.message === "Images uploaded successfully" &&
        response.file_name
      ) {
        const fileName = response.file_name;

        if (fileName) {
          // Update the images state
          setImages((prevImages) => {
            const updatedImages = multiple
              ? [...prevImages, fileName]
              : [fileName];

            // Call the `onImagesUploaded` callback with the updated state
            onImagesUploaded?.(multiple ? updatedImages : fileName);

            return updatedImages;
          });
        } else {
          throw new Error("Upload failed: Missing file name in response");
        }
      } else {
        throw new Error(
          `Upload failed: ${response.message || "Unknown error"}`
        );
      }
    } catch (err) {
      console.error("Upload failed:", err);
      // Reset file input on error
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } finally {
      setUploading(false);
      onStopLoader?.();
    }
  };

  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="OUpload">
      <div className="img-uploader">
        {/* Image preview */}
        <div className="preview">
          {images.map((image, index) => (
            <div className={`img-item ${size}`} key={image || index}>
              {image && image !== "" ? (
                <>
                  <img src={`${imagesBaseUrl}/${image}`} alt="Preview" />
                  <span className="delete" onClick={() => removeImage(image)}>
                    <i className="fas fa-trash"></i>
                  </span>
                </>
              ) : (
                <div className="img-placeholder">
                  {/* <i className="fas fa-file-image"></i> */}
                </div>
              )}
            </div>
          ))}

          {/* Dropzone */}
          <div
            className={`dropzone ${size}`}
            style={{ display: images.length < 1 ? "block" : "none" }}
            onClick={handleDropzoneClick}
          >
            <div className="flex-col-center">
              <i className="fas fa-cloud-upload-alt"></i>
              <p>{text}</p>
              <button
                type="button"
                className="outline mybtn mybtn-default"
                onClick={(e) => e.stopPropagation()}
              >
                Add Image{multiple && "s"}
              </button>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleUpload}
              multiple={multiple}
              accept="image/*,.pdf,.doc,.docx,.xml,application/msword,.xlsx,.xlsm"
              style={{ display: "none" }}
            />
          </div>

          {/* Dropzone for when there's an existing image */}
          <div
            className={`dropzone2 ${size}`}
            style={{ display: images.length >= 1 ? "block" : "none" }}
            onClick={handleDropzoneClick}
          >
            <span className={size}>{multiple ? "+" : "Change"}</span>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleUpload}
              multiple={multiple}
              accept="image/*,.pdf,.doc,.docx,.xml,application/msword,.xlsx,.xlsm"
              style={{ display: "none" }}
            />
          </div>
        </div>
      </div>

      {/* Upload status indicator */}
      {uploading && (
        <div className="upload-status">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Uploading...</span>
        </div>
      )}
    </div>
  );
};

export default OUpload;
